import { DateTime } from 'luxon';

import { MAX_DATA_POINTS, MAX_RANGE_SECONDS, ONE_HOUR, ONE_MINUTE, type PERIOD_TYPE } from 'constants/charting';
import type { HistoricalForecasts, MarketForecastHistoryVo } from 'models/historical_data';
import { HistoricalDataPeriodInterval } from 'models/historical_data';
import type { Frequency } from 'models/series';
import type { CustomPeriodRange } from 'utils/defaultChartPeriods';
import { seededRandom } from 'utils/number';
import { runExtrapolateHistoricalForecast } from 'worker/extrapolate-forecast-history/runExtrapolateHistoricalForecast';

import {
    extrapolateForecastsHistory,
    mapForecastsHistory,
    mapVosToHistoricalForecasts,
} from './forecastHistoryTransform';

const currentUnixTime = (): number => Math.round(new Date().getTime() / 1000);

export const periodIntervalToSeconds = (periodInterval: HistoricalDataPeriodInterval | string): number => {
    const parsed = parseInt(periodInterval, 10);
    return parsed === 0 ? 5 : parsed * ONE_MINUTE;
};

const PERIOD_PATTERN = /^(\d+)(min|mo|q|[hdwy])$/;

/** True for a `{n}{unit}` duration token such as `1w` or `15min` (not for the semantic tokens). */
const isDurationPeriod = (period: string): boolean => PERIOD_PATTERN.test(period);

/** The semantic tokens (`all`, `live`, `game`, `custom`, …): anything that is not a duration. */
const isNonNumericPeriod = (period: PERIOD_TYPE): boolean => !isDurationPeriod(period);
const FALLBACK_PERIOD_SECONDS = 365 * 24 * ONE_HOUR;

const PERIOD_UNIT_SECONDS: Record<string, number> = {
    min: ONE_MINUTE,
    h: ONE_HOUR,
    d: 24 * ONE_HOUR,
    w: 7 * 24 * ONE_HOUR,
    mo: 30 * 24 * ONE_HOUR,
    q: 90 * 24 * ONE_HOUR,
    y: 365 * 24 * ONE_HOUR,
};

const periodToSeconds = (period: PERIOD_TYPE): number | undefined => {
    const match = PERIOD_PATTERN.exec(period);
    if (!match) {
        return undefined;
    }

    const [, amount, unit] = match;
    const unitSeconds = PERIOD_UNIT_SECONDS[unit];
    if (unitSeconds === undefined) {
        return undefined;
    }
    return parseInt(amount, 10) * unitSeconds;
};

const PERIOD_UNIT_MONTHS: Record<string, number> = { mo: 1, q: 3, y: 12 };

/**
 * Calendar months in a month-based duration token (`1mo`, `2q`, `5y`), for windows that walk back whole
 * months rather than elapsed seconds. `undefined` for every other token, the day/week/hour durations and the
 * semantic ones alike.
 */
const periodToMonths = (period: PERIOD_TYPE): number | undefined => {
    const match = /^(\d+)(mo|q|y)$/.exec(period);
    if (!match) {
        return undefined;
    }
    return parseInt(match[1], 10) * PERIOD_UNIT_MONTHS[match[2]];
};

const checkPeriod = (period: PERIOD_TYPE, seriesFrequency?: Frequency): boolean => {
    if (!!seriesFrequency && seriesFrequency === 'daily') {
        return period === '6h' || isNonNumericPeriod(period);
    } else if (!!seriesFrequency && seriesFrequency === 'weekly') {
        return period === '6h' || period === '1d' || isNonNumericPeriod(period);
    } else if (!!seriesFrequency && seriesFrequency === 'monthly') {
        return period === '6h' || period === '1d' || period === '1w' || isNonNumericPeriod(period);
    } else {
        return (
            period === '1h' ||
            period === '3h' ||
            period === '6h' ||
            period === '1d' ||
            period === '1w' ||
            period === '1mo' ||
            isNonNumericPeriod(period)
        );
    }
};

const calculateStartTs = (end: number, firstTsOnData: number | undefined, period: PERIOD_TYPE): number => {
    // Special handling for live/game periods - use a shorter lookback window
    if (period === 'live' || period === 'game') {
        // For live/game, use a maximum 7-hour lookback window
        const maxLookback = MAX_RANGE_SECONDS;
        const calculatedStart = Math.max(firstTsOnData ?? 0, end - maxLookback);
        return calculatedStart;
    }

    if (isNonNumericPeriod(period)) {
        // Pick either first timestamp on data or the desired window size
        return firstTsOnData ? firstTsOnData : end - FALLBACK_PERIOD_SECONDS;
    }

    const periodSeconds = periodToSeconds(period) ?? FALLBACK_PERIOD_SECONDS;
    const initialStart = Math.max(firstTsOnData ?? Number.MIN_VALUE, end - periodSeconds);

    // Avoid starting after the end timestamp
    if (firstTsOnData && initialStart >= end) {
        return firstTsOnData;
    }

    return initialStart;
};

const getStartTsOfPeriodInSeconds = (period: PERIOD_TYPE): number => {
    if (isNonNumericPeriod(period)) return 0;
    const periodSeconds = periodToSeconds(period);
    if (periodSeconds === undefined) return 0;
    return Math.floor(Date.now() / 1000 - periodSeconds);
};

/**
 * The finest bucket size whose point count for `rangeSec` stays under the API's `MAX_DATA_POINTS`
 * cap. Five-second buckets are opt-in: only windowed requests (live/game/custom) are short enough
 * to want them, and they carry their own `MAX_RANGE_SECONDS` clamp.
 */
const periodIntervalForRange = (rangeSec: number, allowFiveSeconds = false): HistoricalDataPeriodInterval => {
    if (allowFiveSeconds && rangeSec <= MAX_RANGE_SECONDS) return HistoricalDataPeriodInterval.FIVE_SECONDS;
    const minPeriodIntervalSec = rangeSec / MAX_DATA_POINTS;
    return minPeriodIntervalSec < ONE_MINUTE
        ? HistoricalDataPeriodInterval.MINUTELY
        : minPeriodIntervalSec < ONE_HOUR
          ? HistoricalDataPeriodInterval.HOURLY
          : HistoricalDataPeriodInterval.DAILY;
};

/**
 * Calculates historical data parameters (start/end timestamps and interval) for charting.
 * Uses minute boundaries for start/end timestamps to improve cache hit rate
 *
 * @param period - The time period to fetch data for
 * @param openDate - Market open date string
 * @param closeDate - Market close date string
 * @returns Object containing periodInterval, startTs, and endTs
 */
const getHistoricalDataParams = (
    period: PERIOD_TYPE,
    openDate: string,
    closeDate: string,
): { periodInterval: HistoricalDataPeriodInterval; startTs: number; endTs: number } => {
    let openTs = new Date(openDate).getTime() / 1000;
    let closeTs = new Date(closeDate).getTime() / 1000;

    // Check if this should use 5-second intervals
    let currentTs = currentUnixTime();

    const isUnopened = openTs > currentTs;
    const isClosed = closeTs < currentTs;

    if (!isUnopened) {
        openTs = openTs - (openTs % ONE_MINUTE);
        closeTs = closeTs - (closeTs % ONE_MINUTE);
        currentTs = currentTs - (currentTs % ONE_MINUTE);
    }

    // Calculate start and end timestamps
    let endSec = Math.ceil(isClosed || isUnopened ? closeTs : currentTs);
    let startSec = calculateStartTs(endSec, openTs, period);

    // For live/game periods, use 5-second intervals with max MAX_RANGE_SECONDS second range
    if (period === 'live' || period === 'game') {
        if (!isClosed && !isUnopened) {
            // Force refresh to get the most current time (bypass cache)
            const latestTs = currentUnixTime();
            endSec = latestTs - (latestTs % ONE_MINUTE);
        } else {
            // For closed/unopened events, just round the close time
            endSec = endSec - (endSec % ONE_MINUTE);
        }

        // Round start timestamp to minute boundary
        startSec = startSec - (startSec % ONE_MINUTE);

        // Apply max range limit — clamp first, then round UP so the range never exceeds MAX_RANGE_SECONDS.
        // Rounding DOWN after the clamp would push startSec below endSec - MAX_RANGE_SECONDS (since
        // MAX_RANGE_SECONDS % ONE_MINUTE === 40), producing a 25020s range (5004 data points) that
        // exceeds the API's 5000-point limit and triggers a 400.
        startSec = Math.max(startSec, endSec - MAX_RANGE_SECONDS);
        const remainder = startSec % ONE_MINUTE;
        if (remainder !== 0) {
            startSec += ONE_MINUTE - remainder;
        }

        return {
            periodInterval: HistoricalDataPeriodInterval.FIVE_SECONDS,
            startTs: startSec,
            endTs: endSec,
        };
    }

    const minPeriodIntervalSec = (endSec - startSec) / MAX_DATA_POINTS;
    const periodInterval = periodIntervalForRange(endSec - startSec);

    // If end time is current time add period interval to end time to get latest data
    if (!isClosed) {
        // Add period interval to end time to get the latest data
        const periodIntervalInSeconds = periodIntervalToSeconds(periodInterval);
        endSec += periodIntervalInSeconds;

        // Round endTs to the end of the hour to improve caching
        const endDateTime = DateTime.fromSeconds(endSec);
        endSec = Math.ceil(endDateTime.endOf('hour').toSeconds());

        // If needed, adjust start time to get the maximum number of data points
        const totalRange = endSec - startSec;
        const maxTimeRange = Math.ceil(periodIntervalInSeconds * (MAX_DATA_POINTS - 1));
        if (totalRange > maxTimeRange) {
            startSec = endSec - maxTimeRange;
        }
    }

    // Round startTs to the end of the hour to improve caching if chart is not hourly
    const startDateTime = DateTime.fromSeconds(startSec);
    const roundedStartSec = Math.ceil(startDateTime.endOf('hour').toSeconds());
    if (roundedStartSec < endSec - minPeriodIntervalSec) {
        startSec = roundedStartSec;
    }

    return {
        periodInterval,
        startTs: startSec,
        endTs: endSec,
    };
};

/**
 * Where a `custom` window ends, in unix seconds: its configured end, else the event close, and
 * never past the close or now. The one rule shared by the fetch range, the resampler and the
 * chart's synthetic seeds. `nowTs` may be omitted where the close is known to be in the past.
 */
const customWindowEndTs = (
    window: Pick<CustomPeriodRange, 'endTs'>,
    closeTs: number,
    nowTs: number = Number.POSITIVE_INFINITY,
): number => Math.min(window.endTs ?? closeTs, closeTs, nowTs);

/**
 * Request parameters for the `custom` period's absolute window (product-metadata's
 * `default_chart_periods` override). Unlike `getHistoricalDataParams` the bounds are given, so
 * this only clamps them — never past the close or now — and picks the finest interval the API's
 * point cap allows. The window is typically hours to a few days, where the `all` request's daily
 * buckets carry no detail (and can end before the window even starts). An empty or inverted
 * window yields zeros, which callers treat as "nothing to fetch".
 */
const getCustomWindowHistoricalDataParams = (
    window: CustomPeriodRange,
    closeDate: string,
): { periodInterval: HistoricalDataPeriodInterval; startTs: number; endTs: number } => {
    const closeTs = new Date(closeDate).getTime() / 1000;
    // Minute boundaries keep the query key (and the server cache) stable across renders.
    const startTs = Math.floor(window.startTs / ONE_MINUTE) * ONE_MINUTE;
    const endTs = Math.ceil(customWindowEndTs(window, closeTs, currentUnixTime()) / ONE_MINUTE) * ONE_MINUTE;
    if (!Number.isFinite(startTs) || !Number.isFinite(endTs) || endTs <= startTs) {
        return { periodInterval: HistoricalDataPeriodInterval.MINUTELY, startTs: 0, endTs: 0 };
    }

    return { periodInterval: periodIntervalForRange(endTs - startTs, true), startTs, endTs };
};

/**
 * Request parameters for a duration period (`1d`, `1w`, `1mo`, …) when its window needs finer
 * buckets than the whole-event `all` request returns — otherwise `undefined`, and the chart
 * windows the `all` data as before. A year-long event's `all` request comes back in daily buckets,
 * so a week drawn from it is seven points. The window runs back `period` from the close or now,
 * and starts on an hour boundary at or before that so the chart's own window is always covered and
 * the query key and server cache stay stable within the hour.
 */
const getDurationWindowHistoricalDataParams = (
    period: PERIOD_TYPE,
    openDate: string,
    closeDate: string,
): { periodInterval: HistoricalDataPeriodInterval; startTs: number; endTs: number } | undefined => {
    const periodSeconds = periodToSeconds(period);
    if (periodSeconds === undefined) return undefined;

    const all = getHistoricalDataParams('all', openDate, closeDate);
    const openTs = new Date(openDate).getTime() / 1000;
    const closeTs = new Date(closeDate).getTime() / 1000;
    const nowTs = currentUnixTime();
    const windowEndTs = Math.min(closeTs, nowTs);
    const windowStartTs = windowEndTs - periodSeconds;
    if (!Number.isFinite(windowStartTs) || windowStartTs <= openTs) return undefined;

    const startTs = windowStartTs - (windowStartTs % ONE_HOUR);
    const periodInterval = periodIntervalForRange(windowEndTs - startTs);
    if (periodIntervalToSeconds(periodInterval) >= periodIntervalToSeconds(all.periodInterval)) return undefined;

    // Same end as the `all` request: the close for a closed event, else one bucket past now rounded
    // to the end of the hour, so the live end of the line is included.
    const endTs =
        closeTs < nowTs
            ? closeTs - (closeTs % ONE_MINUTE)
            : Math.ceil(
                  DateTime.fromSeconds(windowEndTs + periodIntervalToSeconds(periodInterval))
                      .endOf('hour')
                      .toSeconds(),
              );
    return { periodInterval, startTs, endTs };
};

const getLineSamplingPointsNumber = (period: PERIOD_TYPE): number => {
    // Gets the coefficient to be multiplied by a hour unit to calculate for the period window
    switch (period) {
        case '1h': // One point every 30s
            return 120;
        case '3h': // One point every 90s
            return 120;
        case '6h': // One point every 3 minutes
            return 120;
        case '1d': // One point every 10 minutes
            return 144;
        case '1w': // One point every hour
            return 168;
        case '1y': // One point every 2.5 days
            return 730;
        case '2y': // One point every 1 day
            return 730;
        case '5y': // One point every 2.5 days
            return 730;
        default: // 'all' uses the maximum number of points that's feasible in the UI
            return 750;
    }
};

const getCandlestickSamplingPointsNumber = (): number => {
    return 50;
};

const asyncMapForecastsHistory = async (
    forecasts: Array<MarketForecastHistoryVo>,
): Promise<Array<HistoricalForecasts>> => {
    const mappedForecastHistory = mapVosToHistoricalForecasts(forecasts);

    try {
        return await runExtrapolateHistoricalForecast(mappedForecastHistory);
    } catch {
        // Worker failed to load (e.g. module workers unsupported on some mobile browsers).
        // Fall back to main-thread computation.
        return extrapolateForecastsHistory(mappedForecastHistory);
    }
};

const getDeduplicatedIndices = (values: number[]): Set<number> => {
    const deduplicatedIndices = new Set<number>();

    if (values.length === 0) return deduplicatedIndices;

    // Always include the first value
    deduplicatedIndices.add(0);

    let i = 0;
    while (i < values.length) {
        const currentValue = values[i];
        let j = i;

        // Find the end of the current sequence of same values
        while (j < values.length && values[j] === currentValue) {
            j++;
        }

        // Add the last index of the sequence (j-1) if it's different from i
        if (j - 1 > i) {
            deduplicatedIndices.add(j - 1);
        }

        // Move to the next different value
        i = j;

        // Add the first index of the new sequence if it exists
        if (i < values.length) {
            deduplicatedIndices.add(i);
        }
    }

    return deduplicatedIndices;
};

export function addUncertaintyToValues(
    values: number[],
    times: Date[],
    activePeriod: PERIOD_TYPE = 'all',
    volume: number,
    dashedIndex?: number,
): { values: number[]; anchorPoints: Map<number, number> } {
    const config = {
        intensity: 0.3,
        maxDelta: 0.2,
    };
    if (volume > 100000 || activePeriod === 'live' || activePeriod === 'game') {
        return { values, anchorPoints: new Map() };
    }
    const targetPointsPerChart = 100;
    const skipPoints = Math.max(1, Math.floor(values?.length / targetPointsPerChart));
    if (values.length === 0) return { values, anchorPoints: new Map() };

    const deduplicatedIndices = getDeduplicatedIndices(values);
    const valuesHaveLittleChange = deduplicatedIndices.size <= 1;
    if (valuesHaveLittleChange) {
        return { values, anchorPoints: new Map() };
    }

    const uncertaintyValues: number[] = [];

    let seriesMin = values[0];
    let seriesMax = values[0];
    for (let i = 1; i < values.length; i++) {
        const value = values[i];
        if (value < seriesMin) seriesMin = value;
        if (value > seriesMax) seriesMax = value;
    }
    const seriesRange = seriesMax - seriesMin;

    // const periodStartTs = getStartTsOfPeriodInSeconds(activePeriod);

    const calculateUncertainty = (i: number): number => {
        if (deduplicatedIndices.has(i)) {
            return 0;
        }

        // const differenceBetweenPoints = times[i].getTime() - times[i - 1].getTime();
        // const timeOffset = Math.floor(times[i].getTime() / differenceBetweenPoints) - periodStartTs * 1000;
        // Find the time offset since the last price change
        let lastChangeIndex = i - 1;
        while (lastChangeIndex >= 0 && !deduplicatedIndices.has(lastChangeIndex)) {
            lastChangeIndex--;
        }
        let timeOffset = 0;
        if (lastChangeIndex >= 0) {
            timeOffset = times[i].getTime() - times[lastChangeIndex].getTime();
        }

        const seed = Math.floor(timeOffset) + Math.floor(values[i]);

        const randomValues = [
            seededRandom(seed),
            seededRandom(seed + 1),
            seededRandom(seed + 2),
            seededRandom(seed + 3),
        ];
        const [u1, u2, variance1Base, variance2Base] = randomValues;

        const sqrtTerm = Math.sqrt(-2 * Math.log(u1));
        const cosTerm = 2 * Math.PI * u2;
        const normalValue1 = sqrtTerm * Math.cos(cosTerm);
        const normalValue2 = sqrtTerm * Math.sin(cosTerm);

        const scaledNormal = normalValue1 * 0.2;
        const variance1 = variance1Base * 0.3 + 0.7;
        const variance2 = Math.pow(variance2Base, 2);
        const combinedVariance = variance1 * variance2;

        let uncertainty = scaledNormal * config.intensity * seriesRange * combinedVariance;
        const secondaryNormal = normalValue2 * 0.1;
        uncertainty += secondaryNormal * config.intensity * seriesRange * combinedVariance * 0.2;

        const maxDelta = config.maxDelta * seriesRange;
        uncertainty = Math.max(-maxDelta, Math.min(maxDelta, uncertainty));

        return uncertainty;
    };

    const skipPointsMap = new Map<number, number>();
    const anchorPoints = new Map<number, number>();

    for (let i = 0; i < values.length; i++) {
        const isInFinalSegment = dashedIndex === undefined || i >= dashedIndex;
        const isDeduplicated = deduplicatedIndices.has(i);
        if (isInFinalSegment && (isDeduplicated || i % skipPoints === 0)) {
            if (isDeduplicated) {
                skipPointsMap.set(i, values[i]);
                anchorPoints.set(i, 0);
            } else {
                const uncertainty = calculateUncertainty(i);
                const newValue = Math.max(seriesMin, Math.min(seriesMax, values[i] + uncertainty));
                skipPointsMap.set(i, newValue);
                anchorPoints.set(i, uncertainty);
            }
        }
    }

    for (let i = 0; i < values.length; i++) {
        const isInFinalSegment = dashedIndex === undefined || i >= dashedIndex;
        if (!isInFinalSegment) {
            uncertaintyValues.push(values[i]);
        } else if (skipPointsMap.has(i)) {
            uncertaintyValues.push(skipPointsMap.get(i)!);
        } else {
            let prevSkipIndex = -1;
            let nextSkipIndex = -1;
            for (let j = i - 1; j >= 0; j--) {
                if (skipPointsMap.has(j)) {
                    prevSkipIndex = j;
                    break;
                }
            }
            for (let j = i + 1; j < values.length; j++) {
                if (skipPointsMap.has(j)) {
                    nextSkipIndex = j;
                    break;
                }
            }
            if (prevSkipIndex !== -1 && nextSkipIndex !== -1) {
                const prevValue = skipPointsMap.get(prevSkipIndex)!;
                const nextValue = skipPointsMap.get(nextSkipIndex)!;
                const ratio = (i - prevSkipIndex) / (nextSkipIndex - prevSkipIndex);
                const interpolatedValue = prevValue + (nextValue - prevValue) * ratio;
                uncertaintyValues.push(interpolatedValue);
            } else if (prevSkipIndex !== -1) {
                uncertaintyValues.push(skipPointsMap.get(prevSkipIndex)!);
            } else if (nextSkipIndex !== -1) {
                uncertaintyValues.push(skipPointsMap.get(nextSkipIndex)!);
            } else {
                uncertaintyValues.push(values[i]);
            }
        }
    }

    return { values: uncertaintyValues, anchorPoints };
}

export {
    calculateStartTs,
    checkPeriod,
    isDurationPeriod,
    periodToMonths,
    periodToSeconds,
    customWindowEndTs,
    getCandlestickSamplingPointsNumber,
    getDeduplicatedIndices,
    getCustomWindowHistoricalDataParams,
    getDurationWindowHistoricalDataParams,
    getHistoricalDataParams,
    getLineSamplingPointsNumber,
    getStartTsOfPeriodInSeconds,
    mapForecastsHistory,
    asyncMapForecastsHistory,
};
